import { useDeepCompareEffect } from 'ahooks';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';

interface ISwitchAgentItem {
  type: 'switch_agent';
  thinkId: string;
  inputIndex: number;
  done: boolean;
  [key: string]: any;
}

interface IUseAutoExpandHookProps {
  dataSource: any[];
  defaultOpenFirst?: boolean;
  streamingState: string;
}

interface IUseAutoExpandHookReturn {
  expandedIds: string[];
  handleCollapseGroup: (id: string) => void;
  shouldAutoOpenNext: boolean;
  currentActiveIndex: number;
  expandStateRef: React.MutableRefObject<{
    manual: string[];
    closed: string[];
  }>;
  addToManualList: (id: string) => void;
}

/**
 * 自动展开hook，处理复杂的展开逻辑
 * 规则：
1、手打展开，对话完成不需要自动关闭
2、如果有手动展开，需要等展开的内容全部loading完，才可以自动打开下一个loading态，此时下一个loading态，是要根据上一个自动打开的序号，来打开，判定是否已完成，已完成就下一个 序号，依次类推，如果没完成就继续上一个loading态的序号，
3、如果是自动打开，有5轮，第一轮已完成，需要自动打开第二轮，且自动关闭第一轮，
4、按照序号依次打开，而不是跨序号打开，跨序号一定是下一个序号已执行完成的情况下， 才允许跨序号
5、是自动打开的情况下，还在输出过程中，手动关闭，此时，不需要自动开启下一个
 */
export const useSwitchAgentAutoExpand = ({
  dataSource,
  defaultOpenFirst = false,
  streamingState,
}:
IUseAutoExpandHookProps): IUseAutoExpandHookReturn => {
  // 获取所有switch_agent项，并按inputIndex排序
  const switchAgents = useMemo(
    () =>
      dataSource
        .filter(
          (item): item is ISwitchAgentItem => item.type === 'switch_agent',
        )
        .sort((a, b) => a.inputIndex - b.inputIndex),
    [dataSource],
  );
  // 展开状态管理（需要触发UI更新）
  const [expandedIds, setExpandedIds] = useState<string[]>([]);

  // 合并手动展开和手动关闭的记录到一个 ref
  const expandStateRef = useRef<{
    manual: string[];  // 手动展开的ID
    closed: string[];  // 手动关闭的ID
  }>({ manual: [], closed: [] });
  
  // 当前活跃的序号
  const currentActiveIndexRef = useRef<number>(0);

  // 第一个是否打开过。特殊逻辑
  const isFirstOpen = useRef(false);
  // 是否只有一条数据
  const isOnlyOneData = useMemo(
    () =>
      switchAgents.length === 1 &&
      (streamingState !== 'pending'),
    [streamingState, switchAgents.length],
  );
  // 判断是否应该自动打开下一个
  const shouldAutoOpenNext = useMemo(() => {
    if (switchAgents.length === 0) return false;

    // 检查手动展开的项是否已全部完成
    if (
      expandStateRef.current.manual?.length > 0 ||
      expandStateRef.current.closed?.length > 0
    ) {
      // 检查所有手动展开的项是否全部已完成
      const allManualAgentsDone = [
        ...new Set([
          ...expandStateRef.current.manual,
          ...expandStateRef.current.closed,
        ]),
      ].every((manualId) => {
        const manualAgent = switchAgents.find(
          (agent) => agent.thinkId === manualId,
        );
        return manualAgent?.done || false;
      });

      // 如果所有手动展开的项都已完成，就可以自动打开下一个
      if (allManualAgentsDone) {
        // 检查当前活跃序号是否已完成
        const currentAgent = switchAgents.find(
          (agent) => agent.inputIndex === currentActiveIndexRef.current,
        );
        return currentAgent?.done || false;
      }

      return false;
    }

    // 检查当前活跃序号是否已完成
    const currentAgent = switchAgents.find(
      (agent) => agent.inputIndex === currentActiveIndexRef.current,
    );

    // 如果当前项被手动关闭，且尚未完成，不允许自动打开或推进
    if (
      currentAgent &&
      expandStateRef.current.closed.includes(currentAgent.thinkId) &&
      !currentAgent.done
    ) {
      return false;
    }

    if (currentAgent?.done) {
      return true;
    }

    return false;
  }, [switchAgents]);

  // 处理展开/收起
  const handleCollapseGroup = useCallback(
    (id: string) => {
      setExpandedIds((prev) => {
        const isCurrentlyOpen = prev.includes(id);
        if (isCurrentlyOpen) {
          // 收起时，从手动展开记录中移除，并添加到手动关闭记录
          expandStateRef.current.manual = expandStateRef.current.manual.filter(
            (manualId) => manualId !== id,
          );
          if (!expandStateRef.current.closed.includes(id)) {
            expandStateRef.current.closed = [...expandStateRef.current.closed, id];
          }
          return prev.filter((existingId) => existingId !== id);
        } 
        // 展开时，添加到手动展开记录，并从手动关闭记录中移除
        expandStateRef.current.manual = [...expandStateRef.current.manual, id];
        expandStateRef.current.closed = expandStateRef.current.closed.filter(
          (closedId) => closedId !== id,
        );

        // 找到对应的inputIndex，更新当前活跃序号
        const agent = switchAgents.find((a) => a.thinkId === id);
        if (agent) {
          currentActiveIndexRef.current = agent.inputIndex;
        }

        return [...prev, id];
      });
    },
    [switchAgents],
  );

  /**
   * 将指定项添加到手动列表中
   */
  const addToManualList = useCallback((id: string) => {
    if (!expandStateRef.current.manual.includes(id)) {
      expandStateRef.current.manual = [...expandStateRef.current.manual, id];
    }
    // 从手动关闭记录中移除
    expandStateRef.current.closed = expandStateRef.current.closed.filter(
      (closedId) => closedId !== id,
    );
  }, []);
  // 自动展开逻辑
  useDeepCompareEffect(() => {
    if (!shouldAutoOpenNext || switchAgents.length === 0) return;

    // 获取当前活跃的项
    const currentAgent = switchAgents.find(
      (agent) => agent.inputIndex === currentActiveIndexRef.current,
    );

    // 找到当前活跃序号之后第一个未完成的、且不是手动展开、未被手动关闭的switch_agent项
    // 按序号顺序查找，确保不会因为后面项提前完成而跳过中间项
    const nextAgent = switchAgents.find(
      (agent) =>
        !agent.done &&
      !expandStateRef.current.manual.includes(agent.thinkId) &&
      !expandStateRef.current.closed.includes(agent.thinkId),
    );

    if (nextAgent) {
      // 关闭当前活跃的项（如果不是手动展开的）
      // 合并关闭当前项和打开下一项的操作
      setExpandedIds((prev) => {
        const manualItems = prev.filter((id) =>
          expandStateRef.current.manual.includes(id),
        );

        const shouldCloseCurrent =
          currentAgent &&
          !expandStateRef.current.manual.includes(currentAgent.thinkId) &&
          !expandStateRef.current.closed.includes(currentAgent.thinkId);

        const filtered = shouldCloseCurrent && currentAgent
          ? manualItems.filter((id) => id !== currentAgent.thinkId)
          : manualItems;

        return [...filtered, nextAgent.thinkId];
      });

      currentActiveIndexRef.current = nextAgent.inputIndex;
    } else {
      // 没有找到下一个项，说明是最后一项
      // 如果当前项是自动打开的且已完成，自动收起
      if (
        currentAgent?.done &&
        !expandStateRef.current.manual.includes(currentAgent.thinkId)
      ) {
        setExpandedIds((prev) =>
          prev.filter((id) => id !== currentAgent.thinkId),
        );
      }
    }
  }, [shouldAutoOpenNext, switchAgents, expandedIds]);

  // 初始化：第一个switch_agent自动展开
  useEffect(() => {
    if (
      switchAgents.length > 0 &&
      expandedIds.length === 0 &&
      defaultOpenFirst
    ) {
      const firstAgent = switchAgents[0];
      if (
        firstAgent && !firstAgent.done &&
        !expandStateRef.current.closed.includes(firstAgent.thinkId)
      ) {
        setExpandedIds([firstAgent.thinkId]);
        currentActiveIndexRef.current = firstAgent.inputIndex;
      }
    }
  }, [switchAgents, expandedIds?.length, defaultOpenFirst]);

  // 自动处理有且仅一个switch_agent的展开状态
  useEffect(() => {
    if (isOnlyOneData && !isFirstOpen?.current) {
      // 获取当前活跃的项
      const currentAgent = switchAgents.find(
        (agent) => agent.inputIndex === currentActiveIndexRef.current,
      );
      setExpandedIds((prev) =>
        prev.filter((id) => id !== currentAgent?.thinkId),
      );
      isFirstOpen.current = true;
      return;
    }
  }, [isOnlyOneData, switchAgents]);

  return {
    expandedIds,
    handleCollapseGroup,
    shouldAutoOpenNext,
    currentActiveIndex: currentActiveIndexRef.current,
    expandStateRef,
    addToManualList,
  };
};
